import { useCallback, useState } from 'react';
import { Button, Divider, Grid, Input, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
	VALID_ABHYASI_ID_REGEX,
	VALID_EMAIL_REGEX,
	VALID_NAME_REGEX,
	VALID_PHONE_REGEX,
} from '../../../shared/Constants';
import { SearchOutlined } from '@ant-design/icons';
import {
	ApplicantReference,
	BooleanValue,
	Preceptor,
} from '../../../shared/VolunteerApplicationServiceTypes';

const { Text } = Typography;
const { useBreakpoint } = Grid;

interface UpdatePreceptorProps {
	open: boolean;
	onClose: () => void;
	searchPreceptorByAbhyasiId?: (
		abhyasiId: string
	) => Promise<Preceptor | undefined>;
	updatePreceptorDetails?: (
		preceptorDetails: ApplicantReference
	) => Promise<BooleanValue | undefined>;
}

export const UpdatePreceptorModal = ({
	open,
	onClose,
	searchPreceptorByAbhyasiId,
	updatePreceptorDetails,
}: UpdatePreceptorProps) => {
	const { t, i18n } = useTranslation();
	const isLengthyLanguage =
		i18n.language === 'ta' ||
		i18n.language === 'ma' ||
		i18n.language === 'te' ||
		i18n.language === 'ka';
	const screens = useBreakpoint();
	const isMobile = !screens.sm;
	const isStackVertical = isLengthyLanguage && isMobile;
	const [preceptorSrcmIdValue, setPreceptorSrcmId] = useState<
		string | undefined
	>();
	const [preceptorSrcmIdError, setPreceptorSrcmIdError] = useState('');
	const [preceptorFirstName, setPreceptorFirstName] = useState<
		string | undefined
	>();
	const [preceptorFirstNameError, setPreceptorFirstNameError] = useState('');
	const [preceptorMiddleName, setPreceptorMiddleName] = useState<
		string | undefined
	>();
	const [preceptorMiddleNameError, setPreceptorMiddleNameError] = useState('');
	const [preceptorLastName, setPreceptorLastName] = useState<
		string | undefined
	>();
	const [preceptorLastNameError, setPreceptorLastNameError] = useState('');
	const [preceptorEmailValue, setPreceptorEmail] = useState<
		string | undefined
	>();
	const [preceptorEmailError, setPreceptorEmailError] = useState('');
	const [preceptorMobileValue, setPreceptorMobile] = useState<
		string | undefined
	>();
	const [preceptorMobileError, setPreceptorMobileError] = useState('');
	const [preceptorMySrcmPartnerIdValue, setPreceptorMySrcmPartnerId] = useState<
		number | undefined
	>();
	const [fetchedPreceptor, setFetchedPreceptor] = useState<
		Preceptor | undefined
	>();
	const [isPreceptorLoading, setIsPreceptorLoading] = useState<boolean>(false);
	const [isPreceptorUpdating, setIsPreceptorUpdating] =
		useState<boolean>(false);

	const clearFieldErrors = useCallback(() => {
		setPreceptorSrcmIdError('');
		setPreceptorFirstNameError('');
		setPreceptorMiddleNameError('');
		setPreceptorLastNameError('');
		setPreceptorEmailError('');
		setPreceptorMobileError('');
	}, []);

	const clearFieldValues = useCallback(() => {
		setFetchedPreceptor(undefined);
		setPreceptorEmail('');
		setPreceptorMobile('');
		setPreceptorFirstName('');
		setPreceptorMiddleName('');
		setPreceptorLastName('');
		setPreceptorSrcmId('');
		setPreceptorMySrcmPartnerId(undefined);
	}, []);

	const handleClose = useCallback(() => {
		clearFieldValues();
		clearFieldErrors();
		onClose();
	}, [clearFieldErrors, clearFieldValues, onClose]);

	const onPreceptorSRCMIdChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value.toUpperCase();
			setPreceptorSrcmId(value);
			if (!value) {
				clearFieldValues();
				clearFieldErrors();
			}
			if (value && !VALID_ABHYASI_ID_REGEX.test(value)) {
				setPreceptorSrcmIdError('invalidAbhyasiId');
			} else {
				setPreceptorSrcmIdError('');
			}
		},
		[clearFieldErrors, clearFieldValues]
	);

	const handleSearchButtonClick = useCallback(async () => {
		if (!preceptorSrcmIdValue || !searchPreceptorByAbhyasiId) return;
		setIsPreceptorLoading(true);
		const preceptorBySearch = await searchPreceptorByAbhyasiId(
			preceptorSrcmIdValue
		);
		setIsPreceptorLoading(false);
		if (preceptorBySearch) {
			setPreceptorFirstName(preceptorBySearch.firstName || '');
			setPreceptorMiddleName(preceptorBySearch.middleName || '');
			setPreceptorLastName(preceptorBySearch.lastName || '');
			setPreceptorEmail(preceptorBySearch.email);
			setPreceptorMobile(preceptorBySearch.mobile);
			setPreceptorMySrcmPartnerId(preceptorBySearch.mySrcmPartnerId);

			clearFieldErrors();

			setFetchedPreceptor(preceptorBySearch);
		}
	}, [clearFieldErrors, preceptorSrcmIdValue, searchPreceptorByAbhyasiId]);

	const handleUpdateButtonClick = useCallback(async () => {
		if (
			!updatePreceptorDetails ||
			!preceptorFirstName ||
			!preceptorEmailValue ||
			!preceptorMobileValue ||
			!preceptorMySrcmPartnerIdValue
		) {
			return;
		}

		setIsPreceptorUpdating(true);
		const preceptorDetails: ApplicantReference = {
			category: 'PRECEPTOR',
			srcmid: preceptorSrcmIdValue,
			firstName: preceptorFirstName,
			middleName: preceptorMiddleName,
			lastName: preceptorLastName,
			email: preceptorEmailValue,
			mobile: preceptorMobileValue,
			mySrcmPartnerId: preceptorMySrcmPartnerIdValue,
		};
		const isUpdated = await updatePreceptorDetails(preceptorDetails);
		if (isUpdated) {
			handleClose();
		}
		setIsPreceptorUpdating(false);
	}, [
		handleClose,
		preceptorEmailValue,
		preceptorMobileValue,
		preceptorMySrcmPartnerIdValue,
		preceptorFirstName,
		preceptorMiddleName,
		preceptorLastName,
		preceptorSrcmIdValue,
		updatePreceptorDetails,
	]);

	const onPreceptorFirstNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setPreceptorFirstName(value);
			if (value && !VALID_NAME_REGEX.test(value)) {
				setPreceptorFirstNameError('numbersAndSpecialCharactersNotAllowed');
			} else {
				setPreceptorFirstNameError('');
			}
		},
		[]
	);

	const onPreceptorMiddleNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setPreceptorMiddleName(value);
			if (value && !VALID_NAME_REGEX.test(value)) {
				setPreceptorMiddleNameError('numbersAndSpecialCharactersNotAllowed');
			} else {
				setPreceptorMiddleNameError('');
			}
		},
		[]
	);

	const onPreceptorLastNameChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setPreceptorLastName(value);
			if (value && !VALID_NAME_REGEX.test(value)) {
				setPreceptorLastNameError('numbersAndSpecialCharactersNotAllowed');
			} else {
				setPreceptorLastNameError('');
			}
		},
		[]
	);

	const onPreceptorEmailChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setPreceptorEmail(value);
			if (value && !VALID_EMAIL_REGEX.test(value)) {
				setPreceptorEmailError('invalidEmail');
			} else {
				setPreceptorEmailError('');
			}
		},
		[]
	);

	const onPreceptorMobileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			const value = e.target.value;
			setPreceptorMobile(value);
			if (value && !VALID_PHONE_REGEX.test(value)) {
				setPreceptorMobileError('invalidPhoneNo');
			} else {
				setPreceptorMobileError('');
			}
		},
		[]
	);

	return (
		<Modal
			open={open}
			onCancel={handleClose}
			footer={null}
			centered
			className={
				isLengthyLanguage ? 'md:min-w-[800px]' : 'max-w-md sm:max-w-4xl'
			}
			closable={true}
			maskClosable={true}
		>
			<div className="text-center font-sans font-bold text-lg border-b-[2px] border-gray-700 pb-4">
				{t('updatePreceptorDetails')}
			</div>
			<div className="flex flex-col sm:flex-row flex-wrap gap-x-4">
				<div className="mb-[16px] flex flex-col gap-2">
					<Text className="block text-xs text-gray-650">
						{t('searchBySRCMId')}
					</Text>
					<div
						className={`w-full ${
							isMobile
								? 'flex flex-col gap-3 items-center'
								: 'flex items-center gap-5'
						}`}
					>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={preceptorSrcmIdValue}
							allowClear
							onChange={onPreceptorSRCMIdChange}
						/>

						{isMobile && preceptorSrcmIdError && (
							<Text className="text-xs text-red-500 mt-1">
								{t(preceptorSrcmIdError)}
							</Text>
						)}

						<Button
							onClick={() => {
								void handleSearchButtonClick();
							}}
							className={`h-8 text-primary-blue font-sans font-bold ${
								isStackVertical ? 'p-2 mt-3' : ''
							} ${isMobile ? 'w-full' : 'flex-1'}`}
							icon={<SearchOutlined />}
							disabled={!preceptorSrcmIdValue || !!preceptorSrcmIdError}
							loading={isPreceptorLoading}
						>
							{t('search')}
						</Button>
					</div>

					{!isMobile && preceptorSrcmIdError && (
						<Text className="text-xs text-red-500">
							{t(preceptorSrcmIdError)}
						</Text>
					)}
				</div>
				<Divider className="h-[1px] bg-gray-650 my-2" />
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('firstName')}
							<span className="text-red-500"> *</span>
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={preceptorFirstName}
							allowClear
							onChange={onPreceptorFirstNameChange}
							disabled={!fetchedPreceptor}
							maxLength={150}
						/>
						{preceptorFirstNameError && (
							<Text className="text-xs text-red-500">
								{t(preceptorFirstNameError)}
							</Text>
						)}
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('middleName')}
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={preceptorMiddleName}
							allowClear
							onChange={onPreceptorMiddleNameChange}
							disabled={!fetchedPreceptor}
							maxLength={150}
						/>
						{preceptorMiddleNameError && (
							<Text className="text-xs text-red-500">
								{t(preceptorMiddleNameError)}
							</Text>
						)}
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">{t('lastName')}</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={preceptorLastName}
							allowClear
							onChange={onPreceptorLastNameChange}
							disabled={!fetchedPreceptor}
							maxLength={150}
						/>
						{preceptorLastNameError && (
							<Text className="text-xs text-red-500">
								{t(preceptorLastNameError)}
							</Text>
						)}
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('email')}
							<span className="text-red-500"> *</span>
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={preceptorEmailValue}
							allowClear
							onChange={onPreceptorEmailChange}
							disabled={!fetchedPreceptor}
						/>
						{preceptorEmailError && (
							<Text className="text-xs text-red-500">
								{t(preceptorEmailError)}
							</Text>
						)}
					</div>
				</div>
				<div className="w-full sm:w-[calc(50%-0.5rem)]">
					<div className="mb-[16px] flex flex-col gap-2">
						<Text className="block text-xs text-gray-650">
							{t('mobile')}
							<span className="text-red-500"> *</span>
						</Text>
						<Input
							className="flex min-w-[200px] font-sans text-sm text-secondary-gray"
							value={preceptorMobileValue}
							allowClear
							onChange={onPreceptorMobileChange}
							disabled={!fetchedPreceptor}
						/>
						{preceptorMobileError && (
							<Text className="text-xs text-red-500">
								{t(preceptorMobileError)}
							</Text>
						)}
					</div>
				</div>
			</div>
			<div
				className={`flex ${
					isStackVertical
						? 'flex-col items-center space-y-2'
						: 'justify-center gap-2'
				} mt-4 ant-modal-footer`}
			>
				<Button
					className={`flex-1 h-9 text-primary-blue font-sans font-bold ${
						isStackVertical ? 'p-2 w-full' : ''
					}`}
					onClick={handleClose}
				>
					{t('closeFilter')}
				</Button>
				<Button
					type="primary"
					onClick={() => {
						void handleUpdateButtonClick();
					}}
					className={`flex-1 h-9 font-sans font-bold ${
						isStackVertical ? 'p-2 mt-3 w-full' : ''
					}`}
					loading={isPreceptorUpdating}
					disabled={
						!fetchedPreceptor ||
						!preceptorFirstName ||
						!!preceptorFirstNameError ||
						!!preceptorMiddleNameError ||
						!!preceptorLastNameError ||
						!preceptorEmailValue ||
						!!preceptorEmailError ||
						!preceptorMobileValue ||
						!!preceptorMobileError
					}
				>
					{t('update')}
				</Button>
			</div>
		</Modal>
	);
};
